import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { IconButton } from './Button';

/**
 * Modal - Lucky Number Design System
 *
 * Overlay dialog component with focus trap and escape key handling.
 * Dark overlay with cream content card by default.
 * Use dark prop for midnight content on cream backgrounds.
 */

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full mx-4',
};

export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
  footer,
  dark = false, // Use dark modal content (midnight bg)
}) {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store onClose in a ref to avoid re-running effects
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onCloseRef.current();
    }
  };

  // Focus management and scroll lock
  useEffect(() => {
    if (isOpen) {
      // Store current active element
      previousActiveElement.current = document.activeElement;

      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Focus the modal (only once on open)
      modalRef.current?.focus();

      return () => {
        // Restore body scroll
        document.body.style.overflow = '';

        // Restore focus
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen]);

  // Escape key handler (separate effect to avoid focus issues)
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-midnight/80 backdrop-blur-sm animate-fade-in z-0"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Modal content */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className={cn(
          'relative z-10 w-full rounded-xl shadow-lg',
          'animate-scale-in',
          'focus:outline-none',
          dark ? 'bg-midnight border border-cream/10' : 'bg-cream',
          sizes[size],
          className
        )}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className={cn(
            'flex items-start justify-between p-5 border-b',
            dark ? 'border-cream/10' : 'border-midnight/10'
          )}>
            <div>
              {title && (
                <h2
                  id="modal-title"
                  className={cn(
                    'text-lg font-display font-semibold',
                    dark ? 'text-cream' : 'text-midnight'
                  )}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id="modal-description"
                  className={cn(
                    'mt-1 text-sm',
                    dark ? 'text-cream/60' : 'text-midnight/60'
                  )}
                >
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <IconButton
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label="Close modal"
                className={cn(
                  '-mr-1 -mt-1',
                  dark ? 'text-cream/60 hover:text-cream' : 'text-midnight/60 hover:text-midnight'
                )}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </IconButton>
            )}
          </div>
        )}

        {/* Body */}
        <div className={cn(
          'p-5 max-h-[70vh] overflow-y-auto',
          dark ? 'text-cream' : 'text-midnight'
        )}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={cn(
            'flex items-center justify-end gap-3 p-5 border-t',
            dark ? 'border-cream/10' : 'border-midnight/10'
          )}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * ConfirmModal
 *
 * Pre-built confirmation dialog.
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary', // 'primary' | 'danger'
  loading = false,
  dark = false,
}) {
  // Import Button here to avoid circular dependency
  const Button = require('./Button').default;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      dark={dark}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      {message && (
        <p className={dark ? 'text-cream/70' : 'text-midnight/70'}>{message}</p>
      )}
    </Modal>
  );
}
